/**
 * usePdfAttachment
 *
 * Builds a PDF email attachment from form content rendered in the off-screen
 * export portal. Consumers: `FormEmailModal` and `WorkflowParameterContent`.
 *
 * The portal's scope lives here, not in the caller: `visibilityMode` is fixed
 * for the hook's lifetime (SYD-1948) while `splitQuery` is per-generation
 * (SYD-2302), committed with `flushSync` immediately before serialization so the
 * captured DOM is the one this call asked for. On-screen state is never touched.
 *
 * INVARIANT — `generatePdfAttachment` is NOT reentrant. It owns one portal and
 * one piece of state; callers must loop sequentially with `for … await`, never
 * `Promise.all`. Two concurrent calls interleave `flushSync` and capture, and the
 * second target's rows end up in the first target's document.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { usePdfExport } from '../../../pdfExport/use/usePdfExport';
import { usePdfPortal } from '../../../pdfExport/use/usePdfPortal';
import { createPdfAttachment } from '../create/createPdfAttachment';
import type { SplitQuery, VisibilityTypeId } from '#types/System.types';

interface UsePdfAttachmentParams {
  formNumber: string | undefined;
  open: boolean;
  /**
   * Visibility view the PDF renders at (SYD-1948). Drives the export portal via
   * `PdfExportProvider`; the on-screen visibility is never touched.
   */
  visibilityMode?: VisibilityTypeId;
}

interface GeneratePdfAttachmentParams {
  formNumber: string | undefined;
  formTitle: string;
  /**
   * Rows this document is scoped to (SYD-2302). Omit for the whole document —
   * the portal then resets to no split, so a whole-document call is self-scoping
   * even after a per-target run.
   */
  splitQuery?: SplitQuery;
}

interface UsePdfAttachmentReturn {
  generatePdfAttachment: (params: GeneratePdfAttachmentParams) => Promise<{ filename: string; content: string; contentType: string }>;
  isLoadingParams: boolean;
  paramsError: string | null;
  portalNode: ReactNode;
  portalReady: boolean;
}

/**
 * Hook for generating PDF attachments from form content rendered in an
 * export-themed portal at the given `visibilityMode` (SYD-1948) and the
 * per-generation `splitQuery` (SYD-2302).
 */
export const usePdfAttachment = ({ formNumber, open, visibilityMode }: UsePdfAttachmentParams): UsePdfAttachmentReturn => {
  const [portalSplitQuery, setPortalSplitQuery] = useState<SplitQuery | undefined>(undefined);
  const { exportDocument, isLoadingParams, paramsError } = usePdfExport({ formNumber });
  const { portalNode, contentRef, ready: portalReady } = usePdfPortal({
    open,
    visibilityMode,
    splitQuery: portalSplitQuery,
  });

  /**
   * Deps are deliberately `[exportDocument, contentRef]`. `setPortalSplitQuery`
   * is identity-stable and `portalSplitQuery` is never read here, so the callback
   * captured at click time stays valid across every iteration of a fan-out loop.
   */
  const generatePdfAttachment = useCallback(
    async ({ formNumber, formTitle, splitQuery }: GeneratePdfAttachmentParams) => {
      flushSync(() => {
        setPortalSplitQuery(splitQuery);
      });

      const pdfBlob = await exportDocument({
        contentRef,
        outputType: 'blob',
      });

      if (!(pdfBlob instanceof Blob)) {
        throw new Error('Failed to generate PDF blob for email');
      }

      return createPdfAttachment({ pdfBlob, formNumber, formTitle });
    },
    [exportDocument, contentRef],
  );

  return { generatePdfAttachment, isLoadingParams, paramsError, portalNode, portalReady };
};
